import { useEffect } from 'react'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@atoms/Accordion'
import { DropdownMenuItem, DropdownMenuSeparator } from '@atoms/DropdownMenu'
import { DotsMenu } from '@components/molecules/DotsMenu'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

import type { Faq } from './types'

const t = scopedTranslation('help.faqs')
const tButtons = scopedTranslation('shared.buttons')

const demoteHeadings = (html: string) => html.replace(/<(\/?)h[1-6]\b/g, '<$1h4')

type FaqAccordionProps = {
  faqs: Faq[]
  openFaqId: string | null
  canManage: boolean
  onEdit: (faq: Faq) => void
  onMove: (faq: Faq, direction: 'up' | 'down') => void
  onDelete: (faq: Faq) => void
}

export function FaqAccordion({ faqs, openFaqId, canManage, onEdit, onMove, onDelete }: FaqAccordionProps) {
  useEffect(() => {
    if (openFaqId) document.getElementById(`faq-${openFaqId}`)?.scrollIntoView({ block: 'center' })
  }, [openFaqId])

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={openFaqId ? `faq-${openFaqId}` : undefined}
      className="tw:border-t"
    >
      {faqs.map((faq, index) => (
        <AccordionItem key={faq.id} id={`faq-${faq.id}`} value={`faq-${faq.id}`}>
          <div className="tw:grid tw:grid-cols-[1fr_auto] tw:items-center tw:gap-2">
            <AccordionTrigger className="tw:gap-4 tw:data-[state=open]:font-semibold">{faq.title}</AccordionTrigger>
            {canManage && (
              <DotsMenu ariaLabel={t('actions')}>
                <DropdownMenuItem onSelect={() => onEdit(faq)}>
                  <Icon type="pencil-01" />
                  {tButtons('edit')}
                </DropdownMenuItem>
                {index > 0 && (
                  <DropdownMenuItem onSelect={() => onMove(faq, 'up')}>
                    <Icon type="chevron-down" className="tw:rotate-180" />
                    {t('move_up')}
                  </DropdownMenuItem>
                )}
                {index < faqs.length - 1 && (
                  <DropdownMenuItem onSelect={() => onMove(faq, 'down')}>
                    <Icon type="chevron-down" />
                    {t('move_down')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem destructive onSelect={() => onDelete(faq)}>
                  <Icon type="trash-01" />
                  {tButtons('delete')}
                </DropdownMenuItem>
              </DotsMenu>
            )}
          </div>
          <AccordionContent className="tw:max-w-[720px] tw:pb-6">
            <div
              className="tw:leading-relaxed tw:text-gray-cool-700 tw:[&_h4]:mt-4 tw:[&_h4]:font-semibold tw:[&_h4]:text-gray-cool-900 tw:[&_p]:mb-2 tw:[&_ul]:list-disc tw:[&_ul]:pl-5 tw:[&_ol]:list-decimal tw:[&_ol]:pl-5 tw:[&_a]:text-primary-700 tw:[&_a]:underline tw:[&_img]:max-w-[50%]"
              dangerouslySetInnerHTML={{ __html: demoteHeadings(faq.contentHtml) }}
            />
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}
